import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { InjectedFormProps } from 'redux-form';
import { FieldArray, formValueSelector } from 'redux-form';

import AccountsField from 'af-root/scenes/Company/Settings/Members/Shared/AccountsField';

import type UserGroupsSelectionRequestModel from 'ab-requestModels/userGroupsSelection.requestModel';

import { NotificationPriority } from '@acceligentllc/shared/enums/notificationPriority';

import type { RootState } from 'af-reducers';

import * as AccountActions from 'af-actions/accounts';

interface OwnProps {
	formName: string;
	change: InjectedFormProps<UserGroupsSelectionRequestModel>['change'];
	error?: string;
}

interface StateProps {
	includedAccounts: NonNullable<UserGroupsSelectionRequestModel['includedAccounts']>;
	notificationPriority: NonNullable<UserGroupsSelectionRequestModel['notificationPriority']>;
}

type Props = OwnProps & StateProps & ConnectedProps<typeof connector>;

const UserSelectionForNotifyAll: React.FC<Props> = (props: Props) => {

	const {
		change: _change,
		includedAccounts,
		notificationPriority,
		findAllForNotifyAll,
		error,
	} = props;

	const change = React.useMemo(() => _change
		// eslint-disable-next-line react-hooks/exhaustive-deps
		, []);

	const loadAllAccountsForNotifyAll = React.useCallback(async () => {
		const accounts = await findAllForNotifyAll();
		change('includedAccounts', accounts);
	}, [change, findAllForNotifyAll]);

	React.useEffect(() => {
		loadAllAccountsForNotifyAll();
		return () => {
			change('includedAccounts', []);
		};
	}, [change, loadAllAccountsForNotifyAll]);

	const accountsToBeNotifiedLength = React.useMemo(() => {
		if (includedAccounts) {
			const length: number = includedAccounts.reduce((_acc: number, _account: { email: string; phoneNumber: string; }) => {
				const hasEmail = !!_account.email;
				const hasPhoneNumber = !!_account.phoneNumber;

				const willReceiveNotification =
					(notificationPriority === NotificationPriority.EMAIL_ONLY && hasEmail)
					|| (notificationPriority === NotificationPriority.SMS_ONLY && hasPhoneNumber)
					|| [NotificationPriority.EMAIL_FIRST, NotificationPriority.SMS_FIRST, NotificationPriority.SEND_BOTH].includes(notificationPriority)
					&& (hasEmail || hasPhoneNumber);

				return _acc + (willReceiveNotification ? 1 : 0);
			}, 0);
			return length;
		} else {
			return 0;
		}
	}, [includedAccounts, notificationPriority]);

	return (
		<div className="user-groups-selection">
			<div className="m-b-xs">The following <strong className="text-orange">{accountsToBeNotifiedLength}</strong> employees will be notified.</div>
			<div className="m-b-xs">You can also add additional employees. </div>
			<br />
			<FieldArray
				component={AccountsField}
				fixed={false}
				name="includedAccounts"
				notificationPriority={notificationPriority}
			/>
			{error && <span className="help-block"><span className="icon-info"></span>{error}</span>}
		</div >
	);
};

function mapStateToProps(state: RootState, ownProps: OwnProps) {
	const { formName } = ownProps;
	return {
		includedAccounts: formValueSelector(formName)(state, 'includedAccounts'),
		notificationPriority: formValueSelector(formName)(state, 'notificationPriority'),
	};
}

function mapDispatchToProps() {
	return {
		findAllForNotifyAll: AccountActions.findAllForNotifyAll,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(React.memo(UserSelectionForNotifyAll));
